import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import AddIcon from "@material-ui/icons/Add";
import BarChartIcon from "@material-ui/icons/BarChart";
import TuneIcon from "@material-ui/icons/Tune";
import PersonIcon from "@material-ui/icons/Person";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import OpportunityModal from "../../components/OpportunityModal";
import PipelineModal from "../../components/PipelineModal";
import MobileRecordCard from "../../components/MobileRecordCard";
import { AuthContext } from "../../context/Auth/AuthContext";

const ZOOM_KEY = "crmKanbanZoom";
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.0;
const EMPTY_CARD_PREFIX = "__empty__";
const MAX_PAGES = 10; // 40 por página → até 400 negócios no board

const formatBRL = (value) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(0.5),
    flexShrink: 0,
    flexWrap: "wrap",
  },
  zoomLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    minWidth: 34,
    textAlign: "center",
    userSelect: "none",
  },
  boardWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    "& *::-webkit-scrollbar": { width: 4, height: 4 },
    "& *::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.15)"
          : "rgba(0,0,0,0.2)",
    },
    "& *::-webkit-scrollbar-track": { background: "transparent" },
    // react-trello calcula a altura das colunas a partir do conteúdo (não do
    // container pai), então em telas menores o último card acaba cortado
    // mesmo com o scroll interno da coluna no fim. Forçamos a coluna a
    // respeitar a altura real disponível para o scroll interno funcionar.
    "& .smooth-dnd-container.horizontal": {
      height: "100% !important",
    },
    "& .react-trello-lane": {
      height: "100% !important",
      maxHeight: "100% !important",
    },
  },
  boardScaler: {
    transformOrigin: "top left",
    height: "100%",
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,0.5)"
        : "rgba(255,255,255,0.7)",
    zIndex: 10,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": { boxShadow: "0 3px 8px rgba(0,0,0,0.14)" },
    transition: "box-shadow 0.15s ease",
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  emptyCard: {
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    padding: "20px 0",
    textAlign: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    userSelect: "none",
    cursor: "default",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardContact: {
    fontSize: "0.73rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: 4,
  },
  cardUntitled: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    marginBottom: 4,
  },
  cardValue: {
    fontSize: "0.8rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#6FB1FF" : theme.palette.primary.main,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 6,
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  cardUser: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  cardTime: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
  },
  openIcon: {
    padding: 2,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  mobileList: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  mobileStageSelect: {
    minWidth: 130,
  },
}));

// ---------------------------------------------------------------------------
// Card do board (desktop)
// ---------------------------------------------------------------------------
// negócio criado pela automação de entrada nasce com o título igual ao nome
// do contato — nesse caso repetir o nome no card só ocupa espaço
const isUntitled = (opportunity) => {
  const contactName = opportunity.contact?.name?.trim();
  return !!contactName && contactName === opportunity.title?.trim();
};

const OpportunityCard = ({ description, classes, history, onEdit }) => {
  const { opportunity, isEmpty } = description || {};

  if (isEmpty) {
    return <div className={classes.emptyCard}>Nenhum negócio</div>;
  }

  const untitled = isUntitled(opportunity);

  return (
    <div className={classes.card} onClick={() => onEdit(opportunity)}>
      <div className={classes.cardHeader}>
        <Typography className={classes.cardTitle} title={opportunity.title}>
          {opportunity.title}
        </Typography>
        <Tooltip title="Abrir ficha do contato">
          <IconButton
            className={classes.openIcon}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/contacts/${opportunity.contactId}/profile`);
            }}
          >
            <OpenInNewIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>

      {untitled ? (
        <Tooltip title="Clique no card para dar um título e um valor ao negócio">
          <Typography className={classes.cardUntitled}>sem título</Typography>
        </Tooltip>
      ) : (
        <Typography className={classes.cardContact}>
          {opportunity.contact?.name}
        </Typography>
      )}

      {opportunity.value != null && opportunity.value !== "" && (
        <Typography className={classes.cardValue}>
          {formatBRL(opportunity.value)}
        </Typography>
      )}

      <div className={classes.cardFooter}>
        <div className={classes.cardMeta}>
          {opportunity.user?.name && (
            <Typography className={classes.cardUser}>
              <PersonIcon style={{ fontSize: 11 }} />
              {opportunity.user.name}
            </Typography>
          )}
        </div>
        <Typography className={classes.cardTime}>
          {moment(opportunity.updatedAt).fromNow(true)}
        </Typography>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Página CRM — board de negócios por estágio do pipeline
// ---------------------------------------------------------------------------
const OpportunityKanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY));
    return !isNaN(saved) ? saved : 1.0;
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [pipelineModalOpen, setPipelineModalOpen] = useState(false);

  // diálogo de motivo da perda (exigido pelo backend em estágio "lost")
  const [lostDialog, setLostDialog] = useState(null); // { opportunityId, stageId }
  const [lostReason, setLostReason] = useState("");

  const fetchData = useCallback(async (selectedPipelineId) => {
    setLoading(true);
    try {
      const { data: pipelinesData } = await api.get("/pipelines");
      setPipelines(pipelinesData);

      let currentId = selectedPipelineId;
      if (!currentId) {
        const def = pipelinesData.find((p) => p.isDefault) || pipelinesData[0];
        currentId = def?.id || null;
        setPipelineId(currentId);
      }
      if (!currentId) {
        setOpportunities([]);
        return;
      }

      const all = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data } = await api.get("/opportunities", {
          params: { pipelineId: currentId, pageNumber: page },
        });
        all.push(...(data.opportunities || []));
        if (!data.hasMore) break;
      }
      setOpportunities(all);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pipelineId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        // filtro por responsável é opcional
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    const refresh = (data) => {
      if (["update", "create", "delete"].includes(data.action)) {
        fetchData(pipelineId);
      }
    };
    socket.on(`company-${companyId}-opportunity`, refresh);
    socket.on(`company-${companyId}-pipeline`, refresh);

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId]);

  const currentPipeline = pipelines.find((p) => p.id === pipelineId);
  const stages = (currentPipeline?.stages || [])
    .slice()
    .sort((a, b) => a.order - b.order);

  const visibleOpportunities = filterUserId
    ? opportunities.filter((o) => o.userId === filterUserId)
    : opportunities;

  const handleZoom = (delta) => {
    setZoom((prev) => {
      const next = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, Math.round((prev + delta) * 10) / 10)
      );
      localStorage.setItem(ZOOM_KEY, String(next));
      return next;
    });
  };

  const handleEdit = (opportunity) => {
    setEditingOpportunity(opportunity);
    setModalOpen(true);
  };

  const handleModalClose = (saved) => {
    setModalOpen(false);
    setEditingOpportunity(null);
    if (saved) fetchData(pipelineId);
  };

  const moveToStage = async (opportunityId, stageId, reason) => {
    try {
      await api.put(`/opportunities/${opportunityId}/stage`, {
        stageId,
        lostReason: reason,
      });
    } catch (err) {
      toastError(err);
    } finally {
      fetchData(pipelineId);
    }
  };

  const requestMove = (opportunityId, stageId) => {
    const stage = stages.find((s) => s.id === Number(stageId));
    if (!stage) return;
    if (stage.type === "lost") {
      setLostReason("");
      setLostDialog({ opportunityId, stageId: stage.id });
      return;
    }
    moveToStage(opportunityId, stage.id);
  };

  const handleCardMove = (fromLaneId, toLaneId, cardId) => {
    if (cardId.startsWith(EMPTY_CARD_PREFIX)) return;
    if (fromLaneId === toLaneId) return;
    requestMove(Number(cardId), Number(toLaneId));
  };

  const confirmLost = () => {
    if (!lostReason.trim()) {
      toast.error("Informe o motivo da perda.");
      return;
    }
    moveToStage(lostDialog.opportunityId, lostDialog.stageId, lostReason.trim());
    setLostDialog(null);
  };

  const cancelLost = () => {
    setLostDialog(null);
    fetchData(pipelineId); // desfaz o drag visual
  };

  const buildBoard = () => {
    const laneBackground = (color) => {
      if (color) return `${color}22`;
      return theme.palette.type === "dark" ? "#243040" : "#f0f2f5";
    };

    // O react-trello não define cor para o contador do cabeçalho (labelStyle);
    // sem isso ele herda um cinza escuro fixo da lib, pensado pra tema claro,
    // e fica quase ilegível no tema dark.
    const laneLabelStyle = (color) => ({
      color: color || theme.palette.text.primary,
      opacity: theme.palette.type === "dark" ? 0.85 : 0.6,
      fontWeight: 700,
    });

    const lanes = stages.map((stage) => {
      const stageOpps = visibleOpportunities.filter((o) => o.stageId === stage.id);
      const total = stageOpps.reduce(
        (sum, o) => sum + (Number(o.value) || 0),
        0
      );

      const cards =
        stageOpps.length === 0
          ? [
              {
                id: `${EMPTY_CARD_PREFIX}${stage.id}`,
                title: "",
                description: { isEmpty: true },
                draggable: false,
              },
            ]
          : stageOpps.map((opportunity) => ({
              id: String(opportunity.id),
              title: opportunity.title,
              description: { opportunity },
              draggable: true,
            }));

      return {
        id: String(stage.id),
        title: stage.name,
        label: total > 0 ? `${stageOpps.length} · ${formatBRL(total)}` : String(stageOpps.length),
        style: {
          backgroundColor: laneBackground(stage.color),
          borderRadius: 10,
          borderTop: `3px solid ${stage.color || "#ccc"}`,
          height: "100%",
          maxHeight: "100%",
        },
        titleStyle: {
          color: stage.color || theme.palette.text.primary,
          fontWeight: 700,
        },
        // sem isso o contador/total herda a cor escura padrão do react-trello
        // e some no tema escuro
        labelStyle: {
          color: theme.palette.text.primary,
          fontWeight: 600,
        },
        cards,
      };
    });

    return { lanes };
  };

  // ── Visão mobile: lista de cards com seletor de estágio ──
  const renderMobileList = () => (
    <div className={classes.mobileList}>
      {visibleOpportunities.length === 0 && !loading && (
        <Typography color="textSecondary" align="center" style={{ padding: 24 }}>
          Nenhum negócio neste funil ainda.
        </Typography>
      )}
      {stages.map((stage) => {
        const stageOpps = visibleOpportunities.filter((o) => o.stageId === stage.id);
        if (stageOpps.length === 0) return null;
        return (
          <div key={stage.id} style={{ marginBottom: 16 }}>
            <Typography
              variant="subtitle2"
              style={{ color: stage.color, fontWeight: 700, marginBottom: 8 }}
            >
              {stage.name} ({stageOpps.length})
            </Typography>
            {stageOpps.map((opportunity) => (
              <MobileRecordCard
                key={opportunity.id}
                title={opportunity.title}
                subtitle={
                  isUntitled(opportunity)
                    ? "sem título"
                    : opportunity.contact?.name
                }
                headerAction={
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(opportunity)}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                }
                rows={[
                  ...(opportunity.value != null && opportunity.value !== ""
                    ? [{ label: "Valor", value: formatBRL(opportunity.value) }]
                    : []),
                  ...(opportunity.user?.name
                    ? [{ label: "Responsável", value: opportunity.user.name }]
                    : []),
                  {
                    label: "Estágio",
                    value: (
                      <FormControl size="small" className={classes.mobileStageSelect}>
                        <Select
                          value={opportunity.stageId}
                          onChange={(e) =>
                            requestMove(opportunity.id, e.target.value)
                          }
                          disableUnderline
                        >
                          {stages.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ),
                  },
                ]}
              />
            ))}
          </div>
        );
      })}
    </div>
  );

  const boardData = buildBoard();

  return (
    <div className={classes.root}>
      <OpportunityModal
        open={modalOpen}
        onClose={handleModalClose}
        opportunity={editingOpportunity}
      />

      <PipelineModal
        open={pipelineModalOpen}
        onClose={(saved) => {
          setPipelineModalOpen(false);
          if (saved) fetchData(pipelineId);
        }}
        pipeline={currentPipeline}
      />

      {/* Motivo da perda */}
      <Dialog open={!!lostDialog} onClose={cancelLost} maxWidth="xs" fullWidth>
        <DialogTitle>Motivo da perda</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Por que este negócio foi perdido?"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ex: preço, escolheu concorrente..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLost}>Cancelar</Button>
          <Button onClick={confirmLost} color="primary" variant="contained">
            Confirmar perda
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <div className={classes.header}>
        <Typography
          variant="h6"
          style={{ fontWeight: 700, color: theme.palette.text.primary }}
        >
          CRM
        </Typography>

        {/* seletor visível apenas quando a empresa tem mais de um funil */}
        {pipelines.length > 1 && (
          <FormControl size="small" style={{ marginLeft: 12, minWidth: 140 }}>
            <Select
              value={pipelineId || ""}
              onChange={(e) => setPipelineId(e.target.value)}
              disableUnderline
            >
              {pipelines.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" style={{ marginLeft: 12, minWidth: 130 }}>
          <Select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            displayEmpty
            disableUnderline
          >
            <MenuItem value="">Todos os responsáveis</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div style={{ flex: 1 }} />

        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingOpportunity(null);
            setModalOpen(true);
          }}
        >
          Novo negócio
        </Button>

        <Tooltip title="Relatórios do funil">
          <IconButton size="small" onClick={() => history.push("/crm/reports")}>
            <BarChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {isAdmin && (
          <Tooltip title="Configurar etapas do funil">
            <span>
              <IconButton
                size="small"
                onClick={() => setPipelineModalOpen(true)}
                disabled={!currentPipeline}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {!isMobile && (
          <>
            <Tooltip title="Diminuir zoom">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleZoom(-ZOOM_STEP)}
                  disabled={zoom <= ZOOM_MIN}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography className={classes.zoomLabel}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Aumentar zoom">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleZoom(ZOOM_STEP)}
                  disabled={zoom >= ZOOM_MAX}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        <Tooltip
          title={
            <span>
              Cada coluna é um <strong>estágio do funil</strong> e cada card é
              um <strong>negócio</strong> ligado a um contato.
              <br />
              <br />
              Diferente do Kanban de atendimento, o negócio{" "}
              <strong>não recomeça</strong> quando o cliente volta a conversar
              — o estágio fica salvo no contato.
              <br />
              <br />
              Ao arrastar para <strong>Perdido</strong>, o motivo é
              obrigatório. Clique no card para editar.
            </span>
          }
          placement="bottom-end"
          arrow
        >
          <IconButton size="small" style={{ color: theme.palette.text.secondary }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Atualizar board">
          <IconButton
            size="small"
            onClick={() => fetchData(pipelineId)}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Board / lista mobile */}
      {isMobile ? (
        <div className={classes.boardWrapper}>
          {loading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress size={36} />
            </div>
          )}
          {renderMobileList()}
        </div>
      ) : (
        <div className={classes.boardWrapper}>
          {loading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress size={36} />
            </div>
          )}
          <div
            className={classes.boardScaler}
            style={{
              transform: `scale(${zoom})`,
              width: `${(100 / zoom).toFixed(2)}%`,
              height: `${(100 / zoom).toFixed(2)}%`,
            }}
          >
            <Board
              data={boardData}
              onCardMoveAcrossLanes={handleCardMove}
              hideCardDeleteIcon
              style={{
                backgroundColor: "transparent",
                height: "100%",
                padding: "12px 8px",
                alignItems: "stretch",
              }}
              components={{
                Card: (props) => (
                  <OpportunityCard
                    {...props}
                    classes={classes}
                    history={history}
                    onEdit={handleEdit}
                  />
                ),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityKanban;
